import { Random } from 'ht-sdk/std';
import { clamp, invLerp, lerp, remap } from 'ht-sdk/std/calc';

// TODO: unify with getTimeProgress
export function getDateProgress(startDate: Date, endDate: Date) {
	const startTime = startDate.getTime();
	const endTime = endDate.getTime();
	const currentTime = Date.now();
	const timeProgress = invLerp(currentTime, startTime, endTime);

	return timeProgress;
}

export const getTimeProgress = (start: number, end: number, now?: number | null | undefined) => {
	if (now === undefined || now === null) {
		now = Date.now();
	}

	const progress = invLerp(now, start, end);
	const timeSpan = Math.max(0, end - start);
	const timeLeft = clamp(end - now, 0, timeSpan);
	const timeSinceStart = clamp(now - start, 0, timeSpan);

	const started = progress > 0;
	const ended = progress == 1;
	const running = progress > 0 && progress < 1;
	const status = !started ? 'not-started' : ended ? 'ended' : 'running';

	return {
		progress: progress * 100,
		invProgress: (1 - progress) * 100,
		timeLeft: timeLeft / 1000,
		timeSinceStart: timeSinceStart / 1000,
		timeSpan: timeSpan / 1000,
		started: started,
		ended: ended,
		running: running,
		status: status,
	};
};

// TODO: move to ht-sdk
export function randomEntries<T>(arr: T[], count: number): T[] {
	const shuffled = [...arr].sort(() => Math.random() * 2 - 1);
	return shuffled.slice(0, count);
}

// taken from gameBoss
// TODO: move to ht-sdk
/**
 * Creates a debounced function that delays invoking the provided function until
 * after the specified duration has elapsed since the last time the debounced function was invoked.
 *
 * @template T - The type of the function to debounce.
 * @param {T} fn - The function to debounce.
 * @param {number} durationMS - The number of milliseconds to delay invocation.
 * @returns {(...args: Parameters<T>) => void} A new debounced function that delays invoking `fn`.
 *
 * @example
 * const logMessage = (message: string) => {
 *   console.log(message);
 * };
 *
 * const debouncedLogMessage = debounce(logMessage, 1000);
 *
 * debouncedLogMessage('Hello, World!'); // Will log 'Hello, World!' to the console after 1 second if not called again within that time.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(fn: T, durationMS: number): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	return function (...args: Parameters<T>): void {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(() => {
			fn(...args);
		}, durationMS);
	};
}

export function formatTime(duration: number) {
	const hrs = Math.floor(duration / 3600);
	const mins = Math.floor((duration % 3600) / 60);
	const secs = Math.floor(duration % 60);

	const fmtMins = mins.toString().padStart(2, '0');
	const fmtSecs = secs.toString().padStart(2, '0');

	const format = `${hrs > 0 ? hrs + ':' : ''}${fmtMins}:${fmtSecs}`;

	return format;
}

export type StressIndicator = {
	techTeamCount: number;
	/** 0-1 progress of the session */
	timeProgress: number;
	/** Number of open rooms, rooms that are not yet started */
	openRoomCount: number;
	/** Number of rooms that are being repaired */
	occupiedRoomCount: number;
	/** Number of rooms that are being repaired the time is almost up */
	roomAlmostFinishedCount: number;
	/** 0-1 system damage */
	systemDamage: number;
};

export function chooseRoomsCountToOpen(metrics: StressIndicator) {
	// chaos adds random variance to the number of rooms that may be opened
	// chaos starts low, then rapidly ramps up at the end of the game
	// duration 50% -> chaos 0.06
	// duration 75% -> chaos 0.3
	// duration 85% -> chaos 0.5
	// duration 95% -> chaos 0.8
	// duration 100% -> chaos 1
	const chaosCurve = Math.pow(metrics.timeProgress, 6);
	const minChaos = 0.2; // max +20% extra rooms
	const maxChaos = 1.5; // max +150% extra rooms
	let chaos = lerp(minChaos, maxChaos, chaosCurve);

	// reduce chaos when system damage is high
	chaos *= 1 - metrics.systemDamage * 0.4;

	let stress = 0;

	let roomScore = 0.01;
	// room not started yet, high stress
	roomScore += metrics.openRoomCount * 1;
	// in room and can't do anything else, moderate stress
	roomScore += metrics.occupiedRoomCount * 0.8;

	let memberScore = 0.01;
	memberScore += metrics.techTeamCount;
	// room almost finished means member are almost available again
	memberScore += metrics.roomAlmostFinishedCount * 0.4;

	const roomMemberStress = (roomScore / memberScore) * 1;
	stress += roomMemberStress;

	// at the start of the game players are still learning how everything works
	// increase stress a bit so less rooms are opened at the start
	if (metrics.timeProgress < 0.1) {
		stress *= 1.5;
	}

	stress *= 1 + metrics.systemDamage * 0.3;

	const minStressLevel = 0;
	const desiredStressLevel = 1.8;
	const roomsToOpenAtMinStressLevel = metrics.techTeamCount * 1.4;
	const roomsToOpenAtMaxStressLevel = 0;

	const baseRoomsToOpen = remap(stress, minStressLevel, desiredStressLevel, roomsToOpenAtMinStressLevel, roomsToOpenAtMaxStressLevel);

	const maxRoomsToOpen = baseRoomsToOpen + baseRoomsToOpen * chaos;

	// return some intermediate calculated values for debugging purposes
	// the value you actually want to use is `result`

	let result = Math.round(lerp(baseRoomsToOpen, maxRoomsToOpen, Random.range(0, 1)));

	// HACK: current algorithm doesn't work well for low tech team counts
	// open at least 1 room for every tech team that is not in a room
	const minRoomsToOpen = metrics.techTeamCount - metrics.openRoomCount;
	result = Math.max(result, minRoomsToOpen);

	return {
		roomScore,
		memberScore,
		roomMemberStress,
		stress,
		chaos,
		roomsToOpenRange: [baseRoomsToOpen, maxRoomsToOpen],
		result,
	};
}
